import { useState, useCallback, useMemo } from 'react';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocket from '../../../Hooks/useSocket';
import { Resource, Action } from '../../../Constants/socket';
import useVisualHighlight from '../../../Hooks/useVisualHighlight';

/**
 * Custom hook to handle credential real-time socket events.
 *
 * Args:
 *     credentials: Current credentials list.
 *     setCredentials: Function to update credentials.
 *
 * Returns:
 *     Object containing getHighlightClass and softRefreshKey.
 */
const useCredentialSocketEvents = (credentials, setCredentials) => {
    const { socket } = useSocket();
    const [softRefreshKey, setSoftRefreshKey] = useState(0);
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Memoize credential IDs for socket subscription
    const credentialIds = useMemo(() => {
        return credentials.map(credential => credential.id).filter(Boolean);
    }, [credentials]);
    const credentialCursors = useMemo(() => credentials.filter(credential => credential.id).map(credential => ({ id: credential.id, fromTimestamp: credential.updated_at })), [credentials]);

    // Subscribe to credential resource with specific IDs
    useSocketSubscription(Resource.CREDENTIAL, credentialIds.length > 0 ? credentialIds : null, false, null, credentialCursors);

    const handleCredentialCreated = useCallback((data) => {
        // Avoid skeleton flash: do a silent/soft refresh and briefly highlight the new row
        if (data?.id) highlightRow(data.id, 'CREATE');
        setSoftRefreshKey(prev => prev + 1);

        // Subscribe to new credential from its creation time to cover the readiness gap
        if (data?.id && data?.created_at && socket) {
            socket.subscribeToResource(Resource.CREDENTIAL, [data.id], data.created_at);
        }
    }, [highlightRow, socket]);

    const handleCredentialUpdated = useCallback((data) => {
        // Update the specific credential in state without reloading
        setCredentials(prevCredentials =>
            prevCredentials.map(credential =>
                credential.id === data.id ? { ...credential, ...data } : credential
            )
        );
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [setCredentials, highlightRow]);

    const handleCredentialDeleted = useCallback((data) => {
        // Remove the deleted credential from state
        setCredentials(prevCredentials =>
            prevCredentials.filter(credential => credential.id !== data.id)
        );
    }, [setCredentials]);

    // Register socket event listeners
    useSocketEvent(Resource.CREDENTIAL, Action.CREATE, handleCredentialCreated);
    useSocketEvent(Resource.CREDENTIAL, Action.UPDATE, handleCredentialUpdated);
    useSocketEvent(Resource.CREDENTIAL, Action.DELETE, handleCredentialDeleted);

    return {
        getHighlightClass,
        softRefreshKey,
    };
};

export default useCredentialSocketEvents;

